// Wat je volgt.
//
// Bewust op het apparaat zelf, niet achter een login. Het volgen van teams is
// de reden dat iemand de app buiten wedstrijddagen opent, en die functie mag
// niet wachten op authenticatie - die hangt aan fase 4 en aan vragen waar wij
// niet over gaan. Inloggen wordt later wat het hoort te zijn: je favorieten
// meenemen naar een ander apparaat.
//
// Drie soorten, want er zijn drie manieren waarop iemand deze sport volgt:
// een team (de machine van je buurman), een wedstrijd (die je zelf bezoekt) en
// een klasse (waar je de hele competitie van wilt weten).
//
// Farm Stock en Grand National hebben elk hun eigen id-ruimte, dus een team is
// pas uniek met de sectie erbij. Een klasse heeft datzelfde probleem en
// gebruikt dezelfde sleutel als de uitslagen en het klassement: "fs-12",
// "gn-3".

export type Sectie = "fs" | "gn";

export type TeamFavoriet = { soort: "team"; sectie: Sectie; id: number; naam: string };
export type WedstrijdFavoriet = { soort: "wedstrijd"; id: number; naam: string };
export type KlasseFavoriet = { soort: "klasse"; sleutel: string; naam: string };

export type Favoriet = TeamFavoriet | WedstrijdFavoriet | KlasseFavoriet;

export type Soort = Favoriet["soort"];

export const SOORT_LABEL: Record<Soort, string> = {
  team: "Teams",
  wedstrijd: "Wedstrijden",
  klasse: "Klassen",
};

/** Volgorde waarin de soorten op de favorietenpagina staan. */
export const SOORT_VOLGORDE: Soort[] = ["team", "wedstrijd", "klasse"];

/** Enkelvoud, voor een lege sectie of een knoplabel. */
export const SOORT_ENKELVOUD: Record<Soort, string> = {
  team: "team",
  wedstrijd: "wedstrijd",
  klasse: "klasse",
};

const SLEUTEL = "ntto-favorieten";

/** Eén sleutel over alle soorten heen, om op te vergelijken. */
export function favorietSleutel(f: Favoriet): string {
  switch (f.soort) {
    case "team":
      return `team:${f.sectie}-${f.id}`;
    case "wedstrijd":
      return `wedstrijd:${f.id}`;
    case "klasse":
      return `klasse:${f.sleutel}`;
  }
}

export function favorietHref(f: Favoriet): string {
  switch (f.soort) {
    case "team":
      return f.sectie === "fs" ? `/rijder/${f.id}` : `/gn-rijder/${f.id}`;
    case "wedstrijd":
      return `/wedstrijd/${f.id}`;
    case "klasse":
      return "/uitslagen?tab=standen";
  }
}

// --- opslag als externe bron -------------------------------------------
//
// localStorage is voor React een externe bron, dus we ontsluiten hem als
// zodanig: abonneren en een momentopname opvragen. Dat is wat
// useSyncExternalStore verwacht, en het voorkomt de omweg via een effect dat
// na het renderen alsnog state zet.
//
// De momentopname moet tussen twee aanroepen dezelfde verwijzing houden,
// anders blijft React opnieuw renderen. Daarom onthouden we het resultaat en
// lezen we alleen opnieuw na een wijziging.

const LEEG: Favoriet[] = [];

let cache: Favoriet[] | null = null;
const luisteraars = new Set<() => void>();

// Wat er in de opslag stond vóórdat er meer dan teams te volgen was: een rij
// van { sectie, id, naam }, zonder soort. Die mogen niet verdwijnen bij wie de
// app al gebruikt, dus lezen we ze als team.
function leesEen(ruw: unknown): Favoriet | null {
  if (typeof ruw !== "object" || ruw === null) return null;
  const o = ruw as Record<string, unknown>;
  const naam = typeof o.naam === "string" ? o.naam : "";

  if (o.soort === "wedstrijd") {
    return Number.isInteger(o.id) ? { soort: "wedstrijd", id: o.id as number, naam } : null;
  }
  if (o.soort === "klasse") {
    return typeof o.sleutel === "string" && o.sleutel
      ? { soort: "klasse", sleutel: o.sleutel, naam }
      : null;
  }
  // Zonder soort, of soort "team": het oude formaat.
  if ((o.sectie === "fs" || o.sectie === "gn") && typeof o.id === "number") {
    return { soort: "team", sectie: o.sectie, id: o.id, naam };
  }
  return null;
}

function lees(): Favoriet[] {
  try {
    const ruw = window.localStorage.getItem(SLEUTEL);
    if (!ruw) return LEEG;
    const gelezen: unknown = JSON.parse(ruw);
    if (!Array.isArray(gelezen)) return LEEG;
    const geldig = gelezen.map(leesEen).filter((f): f is Favoriet => f !== null);
    return geldig.length > 0 ? geldig : LEEG;
  } catch {
    // Een privévenster of geblokkeerde opslag mag de app niet breken.
    return LEEG;
  }
}

function meld(): void {
  cache = null;
  for (const l of luisteraars) l();
}

export function abonneer(bij: () => void): () => void {
  luisteraars.add(bij);
  // Ook meebewegen met een ander tabblad.
  const opStorage = (e: StorageEvent) => {
    if (e.key === null || e.key === SLEUTEL) meld();
  };
  window.addEventListener("storage", opStorage);
  return () => {
    luisteraars.delete(bij);
    window.removeEventListener("storage", opStorage);
  };
}

export function momentopname(): Favoriet[] {
  cache ??= lees();
  return cache;
}

/** Op de server bestaat er geen opslag; altijd dezelfde lege lijst. */
export function serverMomentopname(): Favoriet[] {
  return LEEG;
}

function bewaar(lijst: Favoriet[]): void {
  try {
    window.localStorage.setItem(SLEUTEL, JSON.stringify(lijst));
  } catch {
    // Niets aan te doen; de app werkt verder gewoon.
  }
  meld();
}

export function wisselFavoriet(f: Favoriet): void {
  const huidig = momentopname();
  const sleutel = favorietSleutel(f);
  const bestaat = huidig.some((x) => favorietSleutel(x) === sleutel);
  bewaar(bestaat ? huidig.filter((x) => favorietSleutel(x) !== sleutel) : [...huidig, f]);
}

export function verwijderFavoriet(f: Favoriet): void {
  const sleutel = favorietSleutel(f);
  bewaar(momentopname().filter((x) => favorietSleutel(x) !== sleutel));
}

/** Alleen de teams, in dezelfde vorm als de rest van de app die kent. */
export function alleenTeams(lijst: Favoriet[]): TeamFavoriet[] {
  return lijst.filter((f): f is TeamFavoriet => f.soort === "team");
}
